import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Phone,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Users,
  BarChart3,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UsagePage() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsage();
  }, []);

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/usage`);
      setUsage(response.data);
    } catch (error) {
      toast.error('Failed to fetch usage data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-48 bg-gray-100" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 bg-gray-100 rounded-xl" />)}
          </div>
          <Skeleton className="h-64 bg-gray-100 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (!usage) return null;

  // Prepare chart data from daily_calls
  const chartData = Object.entries(usage.daily_calls || {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({
      date: date.slice(5), // MM-DD
      calls: count
    }));

  const statCards = [
    { label: 'Total Calls', value: usage.total_calls, icon: Phone, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Completed', value: usage.completed_calls, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Failed', value: usage.failed_calls, icon: XCircle, color: 'text-rose-600', bg: 'bg-rose-50' },
    { label: 'Success Rate', value: `${usage.success_rate}%`, icon: TrendingUp, color: 'text-purple-600', bg: 'bg-purple-50' },
    { label: 'Total Duration', value: `${usage.total_duration_minutes} min`, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Total Cost', value: `$${usage.total_cost}`, icon: DollarSign, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Campaigns', value: usage.total_campaigns + usage.total_bulk_campaigns, icon: BarChart3, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { label: 'Team Members', value: usage.team_member_count, icon: Users, color: 'text-cyan-600', bg: 'bg-cyan-50' },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">Usage</h1>
          <p className="text-gray-500 mt-1">Track your call usage, costs, and performance</p>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <Card key={stat.label} className="bg-white border-gray-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${stat.bg}`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{stat.label}</p>
                    <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Call Status Breakdown */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-gray-900">Call Status Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-4">
              <div className="text-center p-3 rounded-lg bg-emerald-50">
                <p className="text-2xl font-bold text-emerald-600">{usage.completed_calls}</p>
                <p className="text-xs text-gray-500 mt-1">Completed</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-rose-50">
                <p className="text-2xl font-bold text-rose-600">{usage.failed_calls}</p>
                <p className="text-xs text-gray-500 mt-1">Failed</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-orange-50">
                <p className="text-2xl font-bold text-orange-600">{usage.busy_calls}</p>
                <p className="text-xs text-gray-500 mt-1">Busy</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-purple-50">
                <p className="text-2xl font-bold text-purple-600">{usage.noanswer_calls}</p>
                <p className="text-xs text-gray-500 mt-1">No Answer</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-blue-50">
                <p className="text-2xl font-bold text-blue-600">{usage.total_calls - usage.completed_calls - usage.failed_calls - usage.busy_calls - usage.noanswer_calls}</p>
                <p className="text-xs text-gray-500 mt-1">Other</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Daily Calls Chart */}
        {chartData.length > 0 && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-gray-900">Daily Call Volume (Last 30 Days)</CardTitle>
              <CardDescription className="text-gray-500">Number of calls placed per day</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6b7280' }} />
                    <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                    <Tooltip
                      contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    />
                    <Bar dataKey="calls" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
